import { useCallback, useEffect, useRef, useState } from 'react';
import { bomClient } from '../../../client/bomClient';
import {
  BomVersionDetail,
  BomVersionMeta,
  SmartBomComponentEdit,
} from '../../../types/bomVersions';

/**
 * Server state for the Smart BOM version viewer: the version list (loaded as
 * soon as a bomId exists, so the button can show the real latest version
 * number), the selected version's detail, manual-edit saves and the export
 * download. Everything shown comes from the backend — no fabricated numbers.
 */
export interface SmartBomVersionsState {
  versions: BomVersionMeta[] | null;
  latestVersion: number | null;
  selectedVersion: number | null;
  detail: BomVersionDetail | null;
  loadingList: boolean;
  loadingDetail: boolean;
  saving: boolean;
  downloading: boolean;
  error: string | null;
  /** Honest post-save note, e.g. "Saved as v5" or the no-change message. */
  savedNotice: string | null;
  refresh: () => void;
  selectVersion: (version: number) => void;
  saveEdits: (edits: SmartBomComponentEdit[]) => Promise<void>;
  download: () => Promise<void>;
  clearNotice: () => void;
}

export function useSmartBomVersions(bomId: string | null): SmartBomVersionsState {
  const [versions, setVersions] = useState<BomVersionMeta[] | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [detail, setDetail] = useState<BomVersionDetail | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  // Guards a stale response from clobbering a newer selection.
  const detailRequestRef = useRef(0);

  const loadList = useCallback(async (selectLatest: boolean) => {
    if (!bomId) return;
    setLoadingList(true);
    setError(null);
    try {
      const list = await bomClient.listBomVersions(bomId);
      setVersions(list);
      if (selectLatest && list.length > 0) {
        setSelectedVersion(list[list.length - 1].version);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load BOM versions');
    } finally {
      setLoadingList(false);
    }
  }, [bomId]);

  useEffect(() => {
    setVersions(null);
    setSelectedVersion(null);
    setDetail(null);
    if (bomId) loadList(true);
  }, [bomId, loadList]);

  useEffect(() => {
    if (!bomId || selectedVersion === null) return;
    const requestId = ++detailRequestRef.current;
    setLoadingDetail(true);
    setError(null);
    bomClient
      .getBomVersion(bomId, selectedVersion)
      .then((d) => {
        if (detailRequestRef.current === requestId) setDetail(d);
      })
      .catch((e: any) => {
        if (detailRequestRef.current === requestId) setError(e?.message || 'Failed to load BOM version');
      })
      .finally(() => {
        if (detailRequestRef.current === requestId) setLoadingDetail(false);
      });
  }, [bomId, selectedVersion]);

  const selectVersion = useCallback((version: number) => {
    setSavedNotice(null);
    setSelectedVersion(version);
  }, []);

  const saveEdits = useCallback(async (edits: SmartBomComponentEdit[]) => {
    if (!bomId) return;
    setSaving(true);
    setError(null);
    setSavedNotice(null);
    try {
      const result = await bomClient.manualEditBom(bomId, edits);
      if (result.noChanges) {
        setSavedNotice('Nothing changed — no new version was recorded.');
      } else {
        setSavedNotice(`Saved as v${result.version}.`);
        await loadList(false);
        if (result.version) setSelectedVersion(result.version);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save BOM edits');
    } finally {
      setSaving(false);
    }
  }, [bomId, loadList]);

  const latestVersion = versions && versions.length > 0 ? versions[versions.length - 1].version : null;

  // The download is always the LATEST version — the Smart BOM as it stands
  // now — whatever version is being browsed in the sheet. Older versions are
  // for reading the story, not for handing to a supplier.
  const download = useCallback(async () => {
    if (!bomId || latestVersion === null) return;
    setDownloading(true);
    setError(null);
    try {
      await bomClient.exportBomVersion(bomId, latestVersion);
    } catch (e: any) {
      setError(e?.message || 'Failed to download the Smart BOM');
    } finally {
      setDownloading(false);
    }
  }, [bomId, latestVersion]);

  // Stable identities: consumers key effects on these (the checkpoint's
  // open-refresh once looped on a per-render arrow function). A refresh
  // re-selects the latest version: it runs when the sheet is about to open,
  // and the sheet must open on the BOM as it stands now.
  const refresh = useCallback(() => loadList(true), [loadList]);
  const clearNotice = useCallback(() => setSavedNotice(null), []);

  return {
    versions,
    latestVersion,
    selectedVersion,
    detail,
    loadingList,
    loadingDetail,
    saving,
    downloading,
    error,
    savedNotice,
    refresh,
    selectVersion,
    saveEdits,
    download,
    clearNotice,
  };
}

export default useSmartBomVersions;
